import type { PeerPin } from "@/app/climate/_components/ClimatePeerMap";

// Pure projection, formatting, peer-derivation, and chronological-strip
// layout logic behind <ClimatePeerMap>. Extracted from the component so
// the bounding-box projection, date/number formatters, population→radius
// scaling, the CA-pin derivations, and the year-tick / tooltip-clamp
// geometry can be unit-tested in isolation
// (tests/climate/peer-map.test.ts). No React here — the component imports
// these and renders the result. Behaviour is preserved verbatim (this is
// a no-render-change refactor on an indexed public page).

// Canada bounding box for the dot-map projection. Tight enough to keep the
// populated south + west coast filling most of the canvas while still
// including Old Crow YT (lat 67.57) and St. John's NL (lon -52.71).
export const PROJ = {
  lonMin: -141,
  lonMax: -52,
  latMin: 41,
  latMax: 70,
  width: 880,
  height: 380,
} as const;

export function project(lat: number, lon: number): { x: number; y: number } {
  const x = ((lon - PROJ.lonMin) / (PROJ.lonMax - PROJ.lonMin)) * PROJ.width;
  const y = ((PROJ.latMax - lat) / (PROJ.latMax - PROJ.latMin)) * PROJ.height;
  return { x, y };
}

export const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export function fmtBigInt(n: number): string {
  return n.toLocaleString("en-CA");
}

export function fmtMillions(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  return n.toLocaleString("en-CA");
}

const DAY_MS = 1000 * 60 * 60 * 24;
const YEAR_MS = DAY_MS * 365.25;

/** Whole-day gap between two ISO dates (rounded). Negative if `toIso`
 *  precedes `fromIso`. */
export function dayGap(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / DAY_MS);
}

/** Whole-year gap between two ISO dates (rounded, 365.25-day years). */
export function yearGap(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / YEAR_MS);
}

/** Pin radius scaled by √population, clamped so 100+ pins stay legible
 *  and don't blob over Southern Ontario. Falsy population (null or 0) →
 *  fixed 2.5. */
export function pinRadius(population: number | null): number {
  return population
    ? Math.max(2.2, Math.min(6, Math.sqrt(population) / 260))
    : 2.5;
}

export type DerivedPeers = {
  /** All Canadian pins, unsorted (map render order). */
  canadianPins: PeerPin[];
  /** Canadian pins sorted ascending by declaration date (the strip). */
  chrono: PeerPin[];
  calgary: PeerPin | undefined;
  darebin: PeerPin | undefined;
  earliestCa: PeerPin | undefined;
  /** Whole-day gap from the earliest Canadian declaration to Calgary's;
   *  0 when either anchor is missing. */
  calgaryLagDays: number;
};

export function derivePeers(pins: PeerPin[]): DerivedPeers {
  const canadianPins = pins.filter((p) => p.country === "CA");
  const darebin = pins.find((p) => p.id === "darebin");
  const calgary = pins.find((p) => p.id === "calgary");
  // Sort the chronological strip ascending so Calgary lands near the right
  // and the strip reads "early → late."
  const chrono = [...canadianPins].sort((a, b) =>
    a.declared_on.localeCompare(b.declared_on),
  );
  const earliestCa = chrono[0];
  const calgaryLagDays =
    calgary && earliestCa
      ? dayGap(earliestCa.declared_on, calgary.declared_on)
      : 0;
  return { canadianPins, chrono, calgary, darebin, earliestCa, calgaryLagDays };
}

/** Horizontal pixel position of a timestamp on the chronological strip. */
export function chronoX(
  t: number,
  tMin: number,
  span: number,
  padX: number,
  usableW: number,
): number {
  return padX + ((t - tMin) / span) * usableW;
}

/** Year ticks across the strip span. Falls back to the two bookend years
 *  when no Jan-1 boundary lands inside the span (so a sub-year strip still
 *  shows its start/end years). */
export function chronoTicks(
  tMin: number,
  tMax: number,
  span: number,
  padX: number,
  usableW: number,
): { year: number; x: number }[] {
  const yearStart = new Date(tMin).getUTCFullYear();
  const yearEnd = new Date(tMax).getUTCFullYear();
  const ticks: { year: number; x: number }[] = [];
  for (let y = yearStart; y <= yearEnd; y++) {
    const t = Date.UTC(y, 0, 1);
    if (t < tMin || t > tMax) continue;
    ticks.push({ year: y, x: chronoX(t, tMin, span, padX, usableW) });
  }
  if (ticks.length < 2) {
    // Always show the bookend years even if mid-year ticks fall outside span.
    ticks.length = 0;
    ticks.push({ year: yearStart, x: padX });
    ticks.push({ year: yearEnd, x: padX + usableW });
  }
  return ticks;
}

export type ChronoTooltipBox = {
  tx: number;
  ty: number;
  w: number;
  h: number;
  pointerX: number;
};

/** Geometry for the chrono-strip hover tooltip: width estimated from the
 *  longer of name/date, vertically pinned above the dot, and horizontally
 *  clamped to stay inside the strip. The pointer line's x is clamped to
 *  the box interior. */
export function chronoTooltipBox(
  x: number,
  y: number,
  name: string,
  date: string,
  stripWidth: number,
  padX: number,
): ChronoTooltipBox {
  // Estimate width from char count; en-CA names cap around ~30 chars and the
  // date is fixed-width. The estimate doesn't have to be exact — the rect
  // just needs to look like it contains the text. Approx 6.5 px per char at
  // 11px font, plus 16 px of horizontal padding.
  const longest = Math.max(name.length, date.length);
  const w = Math.max(96, longest * 6.5 + 16);
  const h = 38;
  const dotR = 6;
  const gap = 6;
  // Flip vertically: above the dot if there's room, otherwise below.
  // The strip is short enough that "above" basically always works, so just
  // pin it above the baseline.
  const ty = y - dotR - gap - h;
  // Clamp horizontally so the tooltip stays inside the strip.
  let tx = x - w / 2;
  const left = padX - 4;
  const right = stripWidth - padX + 4 - w;
  if (tx < left) tx = left;
  if (tx > right) tx = right;
  // Pointer line from box bottom to dot.
  const pointerX = Math.max(tx + 8, Math.min(tx + w - 8, x));
  return { tx, ty, w, h, pointerX };
}
